import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface RealtimeScore {
  home_score: number;
  away_score: number;
  match_id: string;
}

export interface UseRealtimeScoresReturn {
  scores: RealtimeScore | null;
  loading: boolean;
  error: string | null;
  updateScoreOptimistically: (homeScore: number, awayScore: number) => void;
}

/**
 * Hook for real-time score updates without page refresh
 * Listens to match score changes and updates UI optimistically
 */
export const useRealtimeScores = (matchId: string | undefined): UseRealtimeScoresReturn => {
  const [scores, setScores] = useState<RealtimeScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);

  // Fetch initial scores
  const fetchInitialScores = useCallback(async () => {
    if (!matchId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('matches')
        .select('home_score, away_score, id')
        .eq('id', matchId)
        .single();

      if (fetchError) {
        console.error('Error fetching initial scores:', fetchError);
        setError('Failed to fetch match scores');
        return;
      }

      setScores({
        home_score: data.home_score || 0,
        away_score: data.away_score || 0,
        match_id: matchId
      });

    } catch (err) {
      console.error('Error in fetchInitialScores:', err);
      setError('Error fetching match scores');
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  // Update score optimistically (for immediate UI feedback)
  const updateScoreOptimistically = useCallback((homeScore: number, awayScore: number) => {
    if (!matchId) return;

    setScores(prev => ({
      home_score: homeScore,
      away_score: awayScore,
      match_id: matchId
    }));
  }, [matchId]);

  // Setup real-time subscription for match score updates
  useEffect(() => {
    if (!matchId) return;

    console.log('🔴 Setting up real-time score subscription for match:', matchId);

    // Clean up existing subscription
    if (subscription) {
      supabase.removeChannel(subscription);
    }

    // Create new subscription
    const newSubscription = supabase
      .channel(`match-scores-${matchId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'matches',
          filter: `id=eq.${matchId}`
        },
        (payload) => {
          console.log('🔴 Real-time score update received:', payload);
          
          if (payload.new && payload.new.id === matchId) {
            setScores({
              home_score: payload.new.home_score || 0,
              away_score: payload.new.away_score || 0,
              match_id: matchId
            });
          }
        }
      )
      .subscribe((status) => {
        console.log('🔴 Score subscription status:', status);
      });

    setSubscription(newSubscription);

    // Cleanup function
    return () => {
      console.log('🔴 Cleaning up real-time score subscription');
      if (newSubscription) {
        supabase.removeChannel(newSubscription);
      }
    };
  }, [matchId]);

  // Listen for custom matchScoresUpdated events (from scoresheet updates)
  useEffect(() => {
    const handleMatchScoresUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.matchId === matchId) {
        console.log('🔄 Match scores updated event received, fetching latest scores...');
        fetchInitialScores();
      }
    };

    window.addEventListener('matchScoresUpdated', handleMatchScoresUpdated);
    
    return () => {
      window.removeEventListener('matchScoresUpdated', handleMatchScoresUpdated);
    };
  }, [matchId, fetchInitialScores]);

  // Fetch initial scores on mount
  useEffect(() => {
    fetchInitialScores();
  }, [fetchInitialScores]);

  return {
    scores,
    loading,
    error,
    updateScoreOptimistically
  };
};
